import axios, { type AxiosRequestConfig } from 'axios';
import { env } from '@config/env';
import { useAuthStore } from '../stores/authStore';

/**
 * Sliding refresh of the SSO session token (core mode). Re-mints the 8h session via Core
 * `/portal-auth/refresh-session` so tenant switching + reloads keep working after the session
 * expires (bounded server-side by a 30-day cap). Returns true if a fresh token was stored; a
 * refused refresh rejects, and the caller's request fails with it.
 */
async function refreshSsoSession(): Promise<boolean> {
  if (env.authMode !== 'core') return false;
  const auth = useAuthStore.getState();
  if (!auth.ssoSessionToken) return false;

  const { data } = await axios.post<{ sso?: { session_token?: string } }>(
    `${env.coreApiUrl}/portal-auth/refresh-session`,
    {},
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.ssoSessionToken}`,
        'x-app-template-id': env.appTemplateId,
      },
    },
  );
  const next = data.sso?.session_token;
  if (!next) return false;

  await auth.setSsoSessionToken(next);
  return true;
}

/**
 * Calls authenticated by the Core SSO session token rather than a tenant access token: switching
 * community, joining one, deleting the account, the cross-community gateway.
 *
 * Deliberately not `coreApi` / `tenantApi`: their interceptors would swap in the tenant access
 * token and sign the member out on a 401. Here a 401 means the SSO session expired, so it is
 * slide-refreshed once and the call retried with the new token.
 */
export const ssoSessionApi = axios.create({
  baseURL: env.coreApiUrl,
  timeout: 20_000,
  headers: { 'Content-Type': 'application/json' },
});

ssoSessionApi.interceptors.request.use((config) => {
  config.headers.set('Authorization', `Bearer ${useAuthStore.getState().ssoSessionToken}`);
  config.headers.set('x-app-template-id', env.appTemplateId);
  return config;
});

ssoSessionApi.interceptors.response.use(undefined, async (error: unknown) => {
  if (!axios.isAxiosError(error) || !error.config) throw error;

  const original = error.config as AxiosRequestConfig & { _ssoRetried?: boolean };
  if (error.response?.status !== 401 || original._ssoRetried || !(await refreshSsoSession())) throw error;

  original._ssoRetried = true;
  return ssoSessionApi.request(original);
});
